import './index.css';
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { localeManager, t } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { WalkDockPanelOptions, WalkDockMode, CharacterModel, WalkMode } from './types';
import { getIcon } from '../../utils/icon-manager';

export class WalkDockPanel implements IBimComponent {
    public element!: HTMLElement;
    private options: WalkDockPanelOptions;

    private state = {
        mode: 'none' as WalkDockMode,
        isPlanViewActive: false,
        speed: 1,
        gravity: false,
        collision: false,
        characterModel: 'construction-worker' as CharacterModel,
        walkMode: 'walk' as WalkMode
    };

    private planViewBtn!: HTMLButtonElement;
    private pathModeBtn!: HTMLButtonElement;

    private settingsContainer!: HTMLElement;
    private speedControl!: HTMLElement;
    private speedDecreaseBtn!: HTMLButtonElement;
    private speedIncreaseBtn!: HTMLButtonElement;
    private speedDisplay!: HTMLElement;
    private gravityCheckbox!: HTMLInputElement;
    private gravityLabel!: HTMLElement;
    private collisionCheckbox!: HTMLInputElement;
    private collisionLabel!: HTMLElement;
    private characterModelSelect!: HTMLSelectElement;
    private characterModelLabel!: HTMLElement;
    private walkModeSelect!: HTMLSelectElement;
    private walkModeLabel!: HTMLElement;

    private exitBtn!: HTMLButtonElement;

    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeTheme: (() => void) | null = null;

    constructor(options: WalkDockPanelOptions = {}) {
        this.options = options;
    }

    public init(): void {
        this.element = this.createPanel();
        this.updateSettingsView();

        this.unsubscribeLocale = localeManager.subscribe(() => this.setLocales());
        this.unsubscribeTheme = themeManager.subscribe((theme) => this.setTheme(theme));

        this.setLocales();
        this.setTheme(themeManager.getTheme());
    }

    public setPlanViewActive(active: boolean): void {
        this.state.isPlanViewActive = active;
        this.updateButtonStates();
    }

    public setPathModeActive(active: boolean): void {
        if (!active && this.state.mode !== 'path') {
            return;
        }
        const newMode: WalkDockMode = active ? 'path' : 'none';
        this.setMode(newMode);
    }

    public getState() {
        return { ...this.state };
    }

    public setSpeed(speed: number): void {
        this.state.speed = Math.max(1, Math.min(10, speed));
        this.updateSpeedDisplay();
    }

    public setGravity(enabled: boolean): void {
        this.state.gravity = enabled;
        if (this.gravityCheckbox) {
            this.gravityCheckbox.checked = enabled;
        }
    }

    public setCollision(enabled: boolean): void {
        this.state.collision = enabled;
        if (this.collisionCheckbox) {
            this.collisionCheckbox.checked = enabled;
        }
    }

    private createPanel(): HTMLElement {
        const panel = document.createElement('div');
        panel.className = 'walk-dock-panel';

        const leftButtons = this.createLeftButtons();
        const divider1 = document.createElement('div');
        divider1.className = 'walk-divider';
        this.settingsContainer = this.createSettingsContainer();
        const divider2 = document.createElement('div');
        divider2.className = 'walk-divider';
        const exitBtn = this.createExitButton();

        panel.appendChild(leftButtons);
        panel.appendChild(divider1);
        panel.appendChild(this.settingsContainer);
        panel.appendChild(divider2);
        panel.appendChild(exitBtn);

        return panel;
    }

    private createLeftButtons(): HTMLElement {
        const container = document.createElement('div');
        container.className = 'walk-dock-left';

        this.planViewBtn = this.createIconButton('plan-view', () => {
            this.state.isPlanViewActive = !this.state.isPlanViewActive;
            this.updateButtonStates();
            this.options.onPlanViewToggle?.(this.state.isPlanViewActive);
        });

        this.pathModeBtn = this.createIconButton('path', () => {
            const newMode: WalkDockMode = this.state.mode === 'path' ? 'none' : 'path';
            this.setMode(newMode);
            this.options.onPathModeToggle?.(newMode === 'path');
        });

        container.appendChild(this.planViewBtn);

        return container;
    }

    private createSettingsContainer(): HTMLElement {
        const container = document.createElement('div');
        container.className = 'walk-dock-settings';

        this.speedControl = this.createSpeedControl();

        const gravityWrapper = document.createElement('label');
        gravityWrapper.className = 'walk-checkbox-wrapper walk-checkbox-gravity';
        this.gravityCheckbox = document.createElement('input');
        this.gravityCheckbox.type = 'checkbox';
        this.gravityCheckbox.className = 'walk-checkbox';
        this.gravityCheckbox.checked = this.state.gravity;
        this.gravityCheckbox.addEventListener('change', () => {
            this.state.gravity = this.gravityCheckbox.checked;
            this.options.onGravityToggle?.(this.state.gravity);
        });
        this.gravityLabel = document.createElement('span');
        this.gravityLabel.className = 'walk-checkbox-label';
        gravityWrapper.appendChild(this.gravityCheckbox);
        gravityWrapper.appendChild(this.gravityLabel);

        const collisionWrapper = document.createElement('label');
        collisionWrapper.className = 'walk-checkbox-wrapper walk-checkbox-collision';
        this.collisionCheckbox = document.createElement('input');
        this.collisionCheckbox.type = 'checkbox';
        this.collisionCheckbox.className = 'walk-checkbox';
        this.collisionCheckbox.checked = this.state.collision;
        this.collisionCheckbox.addEventListener('change', () => {
            this.state.collision = this.collisionCheckbox.checked;
            this.options.onCollisionToggle?.(this.state.collision);
        });
        this.collisionLabel = document.createElement('span');
        this.collisionLabel.className = 'walk-checkbox-label';
        collisionWrapper.appendChild(this.collisionCheckbox);
        collisionWrapper.appendChild(this.collisionLabel);

        const characterWrapper = document.createElement('div');
        characterWrapper.className = 'walk-select-wrapper walk-select-wrapper-character-model';
        this.characterModelLabel = document.createElement('label');
        this.characterModelLabel.className = 'walk-select-label';
        this.characterModelSelect = document.createElement('select');
        this.characterModelSelect.className = 'walk-select walk-select-character-model';
        this.characterModelSelect.addEventListener('change', () => {
            this.state.characterModel = this.characterModelSelect.value as CharacterModel;
            this.options.onCharacterModelChange?.(this.state.characterModel);
        });
        characterWrapper.appendChild(this.characterModelLabel);
        characterWrapper.appendChild(this.characterModelSelect);

        const walkModeWrapper = document.createElement('div');
        walkModeWrapper.className = 'walk-select-wrapper walk-select-wrapper-walk-mode';
        this.walkModeLabel = document.createElement('label');
        this.walkModeLabel.className = 'walk-select-label';
        this.walkModeSelect = document.createElement('select');
        this.walkModeSelect.className = 'walk-select walk-select-walk-mode';
        this.walkModeSelect.addEventListener('change', () => {
            this.state.walkMode = this.walkModeSelect.value as WalkMode;
            this.options.onWalkModeChange?.(this.state.walkMode);
        });
        walkModeWrapper.appendChild(this.walkModeLabel);
        walkModeWrapper.appendChild(this.walkModeSelect);

        container.appendChild(this.speedControl);
        container.appendChild(characterWrapper);
        container.appendChild(walkModeWrapper);
        container.appendChild(gravityWrapper);
        container.appendChild(collisionWrapper);

        return container;
    }

    private createSpeedControl(): HTMLElement {
        const container = document.createElement('div');
        container.className = 'walk-speed-control';

        const label = document.createElement('label');
        label.className = 'walk-speed-label';
        label.textContent = t('walkControl.speed');

        const controlGroup = document.createElement('div');
        controlGroup.className = 'walk-speed-group';

        this.speedDecreaseBtn = document.createElement('button');
        this.speedDecreaseBtn.className = 'walk-speed-btn';
        this.speedDecreaseBtn.textContent = '-';
        this.speedDecreaseBtn.addEventListener('click', () => {
            if (this.state.speed > 1) {
                this.state.speed--;
                this.updateSpeedDisplay();
                this.options.onSpeedChange?.(this.state.speed);
            }
        });

        this.speedDisplay = document.createElement('div');
        this.speedDisplay.className = 'walk-speed-display';
        this.speedDisplay.textContent = `${this.state.speed}X`;

        this.speedIncreaseBtn = document.createElement('button');
        this.speedIncreaseBtn.className = 'walk-speed-btn';
        this.speedIncreaseBtn.textContent = '+';
        this.speedIncreaseBtn.addEventListener('click', () => {
            if (this.state.speed < 10) {
                this.state.speed++;
                this.updateSpeedDisplay();
                this.options.onSpeedChange?.(this.state.speed);
            }
        });

        controlGroup.appendChild(this.speedDecreaseBtn);
        controlGroup.appendChild(this.speedDisplay);
        controlGroup.appendChild(this.speedIncreaseBtn);

        container.appendChild(label);
        container.appendChild(controlGroup);

        return container;
    }

    private createIconButton(type: string, onClick: () => void): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.className = `walk-icon-btn walk-icon-btn-${type}`;
        btn.innerHTML = this.getIconSVG(type);
        btn.addEventListener('click', onClick);
        return btn;
    }

    private createExitButton(): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.className = 'walk-exit-btn';
        btn.addEventListener('click', () => {
            this.options.onExit?.();
        });
        this.exitBtn = btn;
        return btn;
    }

    private setMode(mode: WalkDockMode): void {
        const oldMode = this.state.mode;

        if (oldMode === 'walk' && mode !== 'walk') {
            this.options.onWalkModeToggle?.(false);
        }

        if (oldMode === 'path' && mode !== 'path') {
            this.options.onPathModeToggle?.(false);
        }

        this.state.mode = mode;

        if (mode === 'path') {
            this.state.gravity = false;
            this.state.collision = false;
            this.gravityCheckbox.checked = false;
            this.gravityCheckbox.disabled = true;
            this.collisionCheckbox.checked = false;
            this.collisionCheckbox.disabled = true;
        } else {
            this.gravityCheckbox.disabled = false;
            this.collisionCheckbox.disabled = false;
        }

        this.updateButtonStates();
        this.updateSettingsView();
        this.updateSpeedButtonStates();
    }

    private updateButtonStates(): void {
        this.planViewBtn.classList.toggle('active', this.state.isPlanViewActive);
        this.pathModeBtn.classList.toggle('active', this.state.mode === 'path');
    }

    private updateSettingsView(): void {
        const speedWrapper = this.speedControl;
        const gravityWrapper = this.gravityCheckbox.parentElement!;
        const collisionWrapper = this.collisionCheckbox.parentElement!;
        const characterWrapper = this.characterModelSelect.parentElement!;
        const walkModeWrapper = this.walkModeSelect.parentElement!;

        if (this.state.mode === 'walk') {
            speedWrapper.style.display = 'none';
            gravityWrapper.style.display = 'flex';
            collisionWrapper.style.display = 'flex';
            characterWrapper.style.display = 'flex';
            walkModeWrapper.style.display = 'flex';
        } else {
            speedWrapper.style.display = 'flex';
            gravityWrapper.style.display = 'flex';
            collisionWrapper.style.display = 'flex';
            characterWrapper.style.display = 'none';
            walkModeWrapper.style.display = 'none';
        }
    }

    private updateSpeedDisplay(): void {
        this.speedDisplay.textContent = `${this.state.speed}X`;
        this.updateSpeedButtonStates();
    }

    private updateSpeedButtonStates(): void {
        this.speedDecreaseBtn.disabled = this.state.speed <= 1;
        this.speedIncreaseBtn.disabled = this.state.speed >= 10;
    }

    private getIconSVG(type: string): string {
        const icons: Record<string, string> = {
            'plan-view': getIcon('地图'),
            'path': getIcon('路径漫游'),
            'walk': getIcon('第一人称漫游')
        };
        return icons[type] || '';
    }

    public setLocales(): void {
        const speedLabel = this.speedControl.querySelector('.walk-speed-label');
        if (speedLabel) {
            speedLabel.textContent = t('walkControl.speed');
        }

        this.gravityLabel.textContent = t('walkControl.gravity');
        this.collisionLabel.textContent = t('walkControl.collision');

        this.characterModelLabel.textContent = t('walkControl.characterModel.label');
        this.characterModelSelect.innerHTML = '';
        const constructionWorkerOption = document.createElement('option');
        constructionWorkerOption.value = 'construction-worker';
        constructionWorkerOption.textContent = t('walkControl.characterModel.constructionWorker');
        constructionWorkerOption.selected = this.state.characterModel === 'construction-worker';
        this.characterModelSelect.appendChild(constructionWorkerOption);

        const officeMaleOption = document.createElement('option');
        officeMaleOption.value = 'office-male';
        officeMaleOption.textContent = t('walkControl.characterModel.officeMale');
        officeMaleOption.selected = this.state.characterModel === 'office-male';
        this.characterModelSelect.appendChild(officeMaleOption);

        this.walkModeLabel.textContent = t('walkControl.walkMode.label');
        this.walkModeSelect.innerHTML = '';
        const walkOption = document.createElement('option');
        walkOption.value = 'walk';
        walkOption.textContent = t('walkControl.walkMode.walk');
        walkOption.selected = this.state.walkMode === 'walk';
        this.walkModeSelect.appendChild(walkOption);

        const runOption = document.createElement('option');
        runOption.value = 'run';
        runOption.textContent = t('walkControl.walkMode.run');
        runOption.selected = this.state.walkMode === 'run';
        this.walkModeSelect.appendChild(runOption);

        this.exitBtn.textContent = t('walkControl.exit');
    }

    public setTheme(theme: ThemeConfig): void {
        if (!this.element) return;
        const style = this.element.style;

        style.setProperty('--bim-bg-base', theme.bgBase ?? '#152232');
        style.setProperty('--bim-bg-elevated', theme.bgElevated ?? '#1f2d3e');
        style.setProperty('--bim-bg-inset', theme.bgInset ?? '#152232');
        style.setProperty('--bim-border-default', theme.borderDefault ?? '#334155');
        style.setProperty('--bim-border-strong', theme.borderStrong ?? '#475569');
        style.setProperty('--bim-border-subtle', theme.borderSubtle ?? '#1e293b');
        style.setProperty('--bim-divider', theme.divider ?? '#334155');
        style.setProperty('--bim-shadow-xl', theme.shadowXl ?? '0 20px 40px rgba(0, 0, 0, 0.3)');

        style.setProperty('--bim-primary', theme.primary ?? '#3b82f6');
        style.setProperty('--bim-primary-hover', theme.primaryHover ?? '#60a5fa');
        style.setProperty('--bim-primary-subtle', theme.primarySubtle ?? 'rgba(59, 130, 246, 0.15)');

        style.setProperty('--bim-text-primary', theme.textPrimary ?? '#ffffff');
        style.setProperty('--bim-text-inverse', theme.textInverse ?? '#152232');

        style.setProperty('--bim-icon-default', theme.iconDefault ?? '#ffffff');

        style.setProperty('--bim-component-bg-hover', theme.componentBgHover ?? 'rgba(248, 250, 252, 0.06)');
        style.setProperty('--bim-component-bg-active', theme.componentBgActive ?? 'rgba(248, 250, 252, 0.1)');
    }

    public destroy(): void {
        this.unsubscribeLocale?.();
        this.unsubscribeTheme?.();
        if (this.element && this.element.parentElement) {
            this.element.parentElement.removeChild(this.element);
        }
    }
}
